import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatOption } from '@angular/material/core'
import { MatDialog } from '@angular/material/dialog'
import { MatDivider } from '@angular/material/divider'
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatSelect, MatSelectChange } from '@angular/material/select'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { MatTooltip } from '@angular/material/tooltip'
import {
  DataContextBuilder,
  Dimensions,
  ElderCardComponent,
  ElderCardContentDirective,
  ElderDataToolbarComponent,
  ElderDialogService,
  ElderDimensionsInputComponent,
  ElderFormFieldLabelDirective,
  ElderHeaderComponent,
  ElderKeyEventDirective,
  ElderNumberCellDirective,
  ElderQuantityInputControlComponent,
  ElderQuantityPipe,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderThemeDirective,
  ElderToastService,
  ElderToolbarContentDirective,
  ElderValidationErrorDirective,
  Filter,
  IDataContext,
  Quantity,
  Sort,
  Unit,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs'
import { map, mergeMap } from 'rxjs/operators'
import { ProductSalesQuantityPipe } from '../../../../../features/pipes/product-sales-quantity.pipe'
import { ProductSalesTotalWeightPipe } from '../../../../../features/pipes/product-total-weight.pipe'
import { WorkBatchState } from '../../../../orders/batches/work-batch'
import {
  WorkItemPackagingState,
  WorkItem,
  WorkItemUtils,
} from '../../../../orders/work-items/work-item'
import { WorkItemFilter, WorkItemService } from '../../../../orders/work-items/work-item.service'
import { OutputPreviewService } from '../../../../output/preview/output-preview.service'
import { PrintButtonComponent } from '../../../../output/print/print-button/print-button.component'
import { PrintCommandDirective } from '../../../../output/print/print-command.directive'
import { ReportSpec } from '../../../../output/report/report-job'
import { ItemUnitBundle, Package, PackageState, PackagingItem } from '../../../../packages/package'
import { PackagePatch, PackagingItemPatchDto } from '../../../../packages/package-patch'
import { PackageService } from '../../../../packages/package.service'
import { PackagingMaterialTableComponent } from '../../../../packages/packaging-material-table/packaging-material-table.component'
import { PackageType, PackagingMaterialSpec } from '../../../../packages/types/package-type'
import { PackageTypeService } from '../../../../packages/types/package-type.service'
import { StorageOperationsService } from '../../../../storage/operations/storage-operations.service'
import {
  ITerminalBatchPackageItemAssignmentDialogOptions,
  TerminalBatchPackageItemAssignmentDialogComponent,
} from '../terminal-batch-package-item-assignment-dialog/terminal-batch-package-item-assignment-dialog.component'
import { TerminalWorkBatchCtx } from '../terminal-work-batch-ctx'
import {
  addCommand,
  clearCommand,
  ItemUnitBundleCommand,
  removeCommand,
} from '../../../../packages/bundling/item-unit-bundle-command'
import { isPackageSharingEnabled } from '../../../../../../environments/environment'

type QuantifiedItem = [itemId: string, quanitty: Quantity]

@Component({
  selector: 'app-terminal-batch-package-detail',
  templateUrl: './terminal-batch-package-detail.component.html',
  styleUrls: ['./terminal-batch-package-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    ElderCardComponent,
    ElderCardContentDirective,
    FormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatHint,
    ElderQuantityInputControlComponent,
    ElderDimensionsInputComponent,
    MatError,
    ElderValidationErrorDirective,
    ElderFormFieldLabelDirective,
    MatInput,
    MatTabGroup,
    MatTab,
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderKeyEventDirective,
    ElderStopEventPropagationDirective,
    ElderSearchContextDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderNumberCellDirective,
    MatDivider,
    MatButton,
    MatTooltip,
    PackagingMaterialTableComponent,
    ElderHeaderComponent,
    PrintButtonComponent,
    PrintCommandDirective,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
    ProductSalesQuantityPipe,
    ProductSalesTotalWeightPipe,
  ],
})
export class TerminalBatchPackageDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly package$ = new BehaviorSubject<Package>(null)
  public readonly availablePackageTypes$ = new BehaviorSubject<PackageType[]>([])
  public readonly awaitingPackagePatchResponse$ = new BehaviorSubject(false)
  public readonly canModifyPackages$: Observable<boolean>

  public readonly terminalBatchCtx$ = new BehaviorSubject<TerminalWorkBatchCtx>(null)

  public readonly creationMode$ = new BehaviorSubject(false)
  public readonly viewMode$ = new BehaviorSubject(false)

  public readonly labelReport$: Observable<ReportSpec>

  // TODO remove once reload() function on data-context returns an observable
  public readonly batchItemData: IDataContext<WorkItem>
  public readonly forcedFilter$ = new ReplaySubject<Filter[]>(1)
  public readonly awaitingBatchItemDataReload$ = new BehaviorSubject(false)
  private readonly _batchItemDataLoadingTriggered$ = new BehaviorSubject(false)

  private packagePatch: PackagePatch

  public batchItemsSelection: WorkItem[] = []
  public packagedItemsSelection: ItemUnitBundle[] = []

  public readonly Unit = Unit

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  @ViewChild(ElderSearchBoxComponent)
  public searchBox: ElderSearchBoxComponent

  private packageService = inject(PackageService)
  private storageOperationService = inject(StorageOperationsService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)
  private outputPreviewService = inject(OutputPreviewService)
  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const packageTypeService = inject(PackageTypeService)
    const workItemService = inject(WorkItemService)
    this.batchItemData = DataContextBuilder.start<WorkItem>()
      .pageSize(100)
      .autoStartOn({
        requiredFilters: [
          [
            WorkItemFilter.WORK_ORDER_IDS_KEY,
            WorkItemFilter.WORK_ORDER_IDS_KEY,
            WorkItemFilter.WORK_BATCH_ID_KEY,
            WorkItemFilter.PACKAGING_STATES,
          ],
        ],
        initialSort: [new Sort('productionRoute.sequence.processingSequencePos', 'asc')],
      })
      .build(workItemService)

    this.terminalBatchCtx$.pipe(takeUntilDestroyed()).subscribe((ctx) => {
      if (ctx) {
        this.forcedFilter$.next([
          WorkItemFilter.workOrderId(ctx.terminalBatch.workBatch.workOrderId),
          WorkItemFilter.workCenterId(ctx.workCenter.id),
          WorkItemFilter.workBatchId(ctx.terminalBatch.workBatch.id),
          WorkItemFilter.packagingStates([
            WorkItemPackagingState.OPEN,
            WorkItemPackagingState.PARTIAL,
          ]),
        ])

        packageTypeService
          .getCommonTypesByProcessPlanIds(ctx.terminalBatch.workBatch.processPlans)
          .subscribe((commonTypes) => this.availablePackageTypes$.next(commonTypes))
      }
    })

    this.labelReport$ = this.package$.pipe(map((pkg) => ReportSpec.packageLabels(pkg.id)))

    combineLatest([this.terminalBatchCtx$, this.package$])
      .pipe(takeUntilDestroyed())
      .subscribe(([ctx, pkg]) => {
        this.viewMode$.next(
          (ctx && ctx.terminalBatch.workBatch.state !== WorkBatchState.PROCESSING) ||
            (pkg && pkg.state !== PackageState.PREVIEW)
        )
      })

    combineLatest([
      this.awaitingBatchItemDataReload$,
      this.batchItemData.loading,
      this._batchItemDataLoadingTriggered$,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([awaitingResponse, ctxLoading, ctxLoadingTriggered]) => {
        if (awaitingResponse && ctxLoading && !ctxLoadingTriggered) {
          this._batchItemDataLoadingTriggered$.next(true)
        } else if (awaitingResponse && !ctxLoading && ctxLoadingTriggered) {
          this._batchItemDataLoadingTriggered$.next(false)
          this.awaitingBatchItemDataReload$.next(false)
        }
      })

    this.canModifyPackages$ = combineLatest([
      this.awaitingBatchItemDataReload$,
      this.awaitingPackagePatchResponse$,
    ]).pipe(
      takeUntilDestroyed(),
      map(
        ([awaitingBatchItemDataReload, awaitingResponse]) =>
          !awaitingResponse && !awaitingBatchItemDataReload
      )
    )
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get package(): Package {
    return this.package$.getValue()
  }

  @Input()
  public set package(pkg: Package) {
    this.package$.next(pkg)
    this.creationMode$.next(!pkg.id)
    this.viewMode$.next(pkg.state !== PackageState.PREVIEW)
    this.packagePatch = new PackagePatch(pkg.id)
  }

  @Input()
  public set terminalBatchCtx(ctx: TerminalWorkBatchCtx) {
    this.terminalBatchCtx$.next(ctx)
  }

  public get terminalBatchCtx(): TerminalWorkBatchCtx {
    return this.terminalBatchCtx$.getValue()
  }

  public get canDelete$(): Observable<boolean> {
    return combineLatest([this.creationMode$, this.viewMode$, this.package$]).pipe(
      map(
        ([creationMode, viewMode, pkg]) =>
          !creationMode &&
          !viewMode &&
          pkg.state === PackageState.PREVIEW &&
          (!pkg.items || pkg.items.length === 0) &&
          this.containsNoOtherBatches(pkg, this.terminalBatchCtx) // redundant, added in case deletion with items is allowed in the future
      )
    )
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public allowPackagingEdit(): boolean {
    return this.package.type.supportedPackagingMaterials?.length > 0
  }

  public canPreviewPackageLabel(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.workBatch.state === WorkBatchState.PROCESSING
  }

  public onPreviewPackageLabel(pkg: Package): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.packageLabels(pkg.id))
  }

  public canAdd(item: WorkItem): boolean {
    return !WorkItemUtils.isCompletelyPackaged(item)
  }

  public addPartially(pkg: Package, item: WorkItem): void {
    this.dialog
      .open(TerminalBatchPackageItemAssignmentDialogComponent, {
        minWidth: '40vw',
        height: '30vh',
        disableClose: true,
        autoFocus: true,
        data: <ITerminalBatchPackageItemAssignmentDialogOptions>{
          pkg: pkg,
          item: item,
          packagedPiecesCount: WorkItemUtils.calcPackagedQuantity(item),
          unPackagedPiecesCount: WorkItemUtils.calcUnPackagedQuantity(item),
        },
      })
      .afterClosed()
      .subscribe((quantity: Quantity) => {
        if (quantity) {
          this.addToPackage(pkg, [[item.id, quantity]])
        }
      })
  }

  public addCompletely(pkg: Package, item: WorkItem): void {
    this.addAllCompletely(pkg, [item])
  }

  public addAllCompletely(pkg: Package, items: WorkItem[]): void {
    this.addToPackage(
      pkg,
      items.map((item) => {
        const maxPackableQuantity = Quantity.from(
          item.orderedProduct.toSales.unit,
          WorkItemUtils.calcUnPackagedQuantity(item)
        )
        return [item.id, maxPackableQuantity]
      })
    )
  }

  public isEmpty(selection: any[]): boolean {
    return !selection || selection.length === 0
  }

  public canClearPackageContent(pkg: Package, ctx: TerminalWorkBatchCtx): boolean {
    const containsNoOtherBatches = this.containsNoOtherBatches(pkg, ctx)
    return pkg.items.length > 0 && containsNoOtherBatches
  }

  containsNoOtherBatches(pkg: Package, ctx: TerminalWorkBatchCtx): boolean {
    const nonBatchBundles = pkg.items.filter(
      (bundle) => bundle.workBatchId !== ctx.terminalBatch.id
    ).length
    const containsNoOtherBatches = nonBatchBundles === 0
    return containsNoOtherBatches
  }

  public clearPackageContent(pkg: Package): void {
    const workBatchId = this.terminalBatchCtx.terminalBatch.workBatch.id
    const cmd = clearCommand(workBatchId, pkg.items)
    this.replacePackagedItems(pkg, cmd)
  }

  public removeFromPackage(pkg: Package, toRemove: ItemUnitBundle[]): void {
    const workBatchId = this.terminalBatchCtx.terminalBatch.workBatch.id
    const cmd = removeCommand(workBatchId, toRemove)
    this.replacePackagedItems(pkg, cmd)
  }

  public onPackageTypeChanged(selectChange: MatSelectChange): void {
    const newTypeId = selectChange.value

    this.possiblyApplyTypePresets(newTypeId)

    this.package.typeId = newTypeId
  }

  public onPackagingItemsChange(items: PackagingItem[]): void {
    this.packagePatch.packagingItems = items.map(
      (item) => new PackagingItemPatchDto(item.material.materialId, item.material.quantity)
    )
  }

  packageSharingEnabled(ctx: TerminalWorkBatchCtx): boolean {
    const workBatch = ctx.terminalBatch.workBatch
    return isPackageSharingEnabled() && workBatch.productionLine.packaging.packageSharingEnabled
  }

  isForeign(bundle: ItemUnitBundle, terminalBatchCtx: TerminalWorkBatchCtx): boolean {
    return !this.isOwnedByBatch(bundle, terminalBatchCtx)
  }

  isOwnedByBatchEvaluator(
    terminalBatchCtx: TerminalWorkBatchCtx
  ): (bundle: ItemUnitBundle) => boolean {
    return (bundle: ItemUnitBundle) => this.isOwnedByBatch(bundle, terminalBatchCtx)
  }

  isOwnedByBatch(bundle: ItemUnitBundle, terminalBatchCtx: TerminalWorkBatchCtx): boolean {
    return bundle.workBatchId === terminalBatchCtx?.terminalBatch?.id
  }

  /***************************************************************************
   *                                                                         *
   * Public API: WorkItem                                                    *
   *                                                                         *
   **************************************************************************/

  public getProductionPriority(item: WorkItem): number {
    return WorkItemUtils.getProcessingSequencePos(item, this.terminalBatchCtx?.workCenter?.id)
  }

  public getSortByProcessingSequencePosString(): string {
    return WorkItemUtils.getSortByProcessingSequencePosString(
      this.terminalBatchCtx?.terminalBatch?.workBatch?.productionLine,
      this.terminalBatchCtx?.workCenter?.id
    )
  }

  /***************************************************************************
   *                                                                         *
   * Search Box                                                              *
   *                                                                         *
   **************************************************************************/

  public onItemSearchEnter(event: KeyboardEvent): void {
    if (this.canAddItemOnSearch()) {
      const items = this.batchItemData.snapshot.data
      if (items.length === 1) {
        const item = items[0]
        if (this.canAdd(item)) {
          this.addAllCompletely(this.package, [item])
          this.searchBox.clearSearch(event)
        }
      }
    }
  }

  private canAddItemOnSearch(): boolean {
    return (
      this.batchItemData.isStarted &&
      this.batchItemData.filter.filtersSnapshot.some(
        (f) => f.key === WorkItemFilter.NUMBERS_KEY && f.value
      )
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private addToPackage(pkg: Package, toAdd: QuantifiedItem[]): void {
    const workBatchId = this.terminalBatchCtx.terminalBatch.workBatch.id
    const addCmd = addCommand(workBatchId, toAdd)
    this.replacePackagedItems(pkg, addCmd)
  }

  private replacePackagedItems(pkg: Package, cmd: ItemUnitBundleCommand) {
    this.awaitingPackagePatchResponse$.next(true)
    this.packageService.executeBundleCommand(pkg, cmd).subscribe({
      next: (updated) => {
        this.package = updated
        this.reloadBatchItemData() // TODO: should also trigger package reload in parent to avoid missed model updates
        this.awaitingPackagePatchResponse$.next(false)
      },
      error: (err) => {
        this.toastService.error('Failed to update items!', err)
        this.awaitingPackagePatchResponse$.next(false)
      },
    })
  }

  private reloadBatchItemData(): void {
    this.awaitingBatchItemDataReload$.next(true)
    this.batchItemData.reload()
  }

  private possiblyApplyTypePresets(newTypeId: string): void {
    const availablePackageTypes = this.availablePackageTypes$.getValue()
    const previousType = availablePackageTypes.find((t) => t.id === this.package.typeId)
    const newType = availablePackageTypes.find((t) => t.id === newTypeId)

    this.possiblyApplyPresetDimensions(
      previousType.presetDimensions,
      newType.presetDimensions,
      this.package.dimensions
    )

    this.applyPresetPackagingMaterials(newType.supportedPackagingMaterials)
  }

  private possiblyApplyPresetDimensions(
    previousPresetDimensions: Dimensions,
    newPresetDimensions: Dimensions,
    currentDimensions: Dimensions
  ): void {
    if (this.isEquals(previousPresetDimensions, currentDimensions)) {
      this.package.dimensions = newPresetDimensions
    }
  }

  // TODO move to dedicated util class or to library
  private isEquals(dimA: Dimensions, dimB: Dimensions): boolean {
    this.log.info('Comparing', [dimA, dimB])

    if (!dimA && !dimB) {
      return true
    }

    if (!dimA || !dimB) {
      return false
    }

    return (
      dimA.length === dimB.length &&
      dimA.width === dimB.width &&
      dimA.height === dimB.height &&
      dimA.unit === dimB.unit
    )
  }

  private applyPresetPackagingMaterials(supportedMaterials: PackagingMaterialSpec[]): void {
    this.package.type.supportedPackagingMaterials = supportedMaterials
    this.package.packagingItems = []
    this.packagePatch.packagingItems = []
  }

  private save(): void {
    if (this.creationMode$.getValue()) {
      this.packageService.create(this.package).subscribe({
        next: (pkg) => (this.package = pkg),
        error: (err) => this.toastService.error('messages.creation_failed', err),
      })
    } else {
      PackagePatch.applyPackageData(this.packagePatch, this.package)

      this.packageService.updateByPatch(this.packagePatch).subscribe({
        next: (pkg) => {
          this.package = pkg
          this.toastService.info('messages.persisting_successful')
        },
        error: (err) => this.toastService.error('messages.saving_failed', err),
      })
    }
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'messages.deleting_confirmation_msg',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.storageOperationService
            .retract([this.package.id])
            .pipe(mergeMap(() => this.packageService.delete(this.package)))
            .subscribe({
              next: (_) => this.close.emit(),
              error: (err) => this.toastService.error('messages.deleting_failed', err),
            })
        }
      })
  }

  private emitClose(event: Event): void {
    this.close.emit(event)
  }
}
